import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Briefcase, Check, X, ArrowLeft, Crown, Zap, Star, Users, User, CreditCard, Shield, ArrowRight } from 'lucide-react';
import { Button } from '../components/ui/button';
import { useAuth, API } from '../App';
import { toast } from 'sonner';
import { TRAINER_PLANS, COMPANY_PLANS, BILLING_PERIODS, getAllPlans, getMonthlyEquivalent } from '../data/subscriptionPlans';
import { SEO } from '../components/SEO';

const PLAN_ICONS = {
  basic: Shield,
  professional: Zap,
  premium: Crown
};

const PLAN_COLORS = {
  basic: { bg: 'bg-slate-50', border: 'border-slate-200', accent: 'text-slate-600', btn: 'bg-slate-800 hover:bg-slate-900', badge: 'bg-slate-100 text-slate-600' },
  professional: { bg: 'bg-blue-50', border: 'border-blue-300', accent: 'text-blue-600', btn: 'bg-blue-600 hover:bg-blue-700', badge: 'bg-blue-100 text-blue-700' },
  premium: { bg: 'bg-purple-50', border: 'border-purple-300', accent: 'text-purple-600', btn: 'bg-purple-600 hover:bg-purple-700', badge: 'bg-purple-100 text-purple-700' }
};

function PlanCard({ plan, billing, userType, isCurrentPlan, onSelect }) {
  const colors = PLAN_COLORS[plan.id] || PLAN_COLORS.basic;
  const Icon = PLAN_ICONS[plan.id] || Shield;
  const isAnnual = billing === 'annual';
  const isFree = plan.priceINR === 0;

  const priceINR = isAnnual ? plan.annualPriceINR : plan.priceINR;
  const priceUSD = isAnnual ? plan.annualPriceUSD : plan.priceUSD;
  const monthlyEq = getMonthlyEquivalent(plan, billing);

  return (
    <div
      className={`relative rounded-2xl border-2 ${colors.border} ${colors.bg} p-6 flex flex-col transition-all hover:shadow-lg ${
        plan.popular ? 'ring-2 ring-blue-500 ring-offset-2' : ''
      }`}
      data-testid={`plan-card-${plan.id}`}
    >
      {plan.popular && (
        <div className="absolute -top-3.5 left-1/2 -translate-x-1/2">
          <span className="px-4 py-1 bg-blue-600 text-white text-xs font-bold rounded-full uppercase tracking-wide">
            Most Popular
          </span>
        </div>
      )}

      <div className="flex items-center gap-3 mb-4">
        <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${colors.badge}`}>
          <Icon className="w-5 h-5" />
        </div>
        <div>
          <h3 className="font-outfit font-bold text-lg text-slate-900">{plan.name}</h3>
          <p className="text-slate-500 text-xs">{plan.description}</p>
        </div>
      </div>

      {/* Pricing */}
      <div className="mb-5">
        {isFree ? (
          <div className="flex items-baseline gap-1">
            <span className="text-4xl font-bold text-slate-900">Free</span>
            <span className="text-slate-500 text-sm">forever</span>
          </div>
        ) : (
          <div>
            <div className="flex items-baseline gap-1">
              <span className="text-3xl font-bold text-slate-900">₹{priceINR.toLocaleString('en-IN')}</span>
              <span className="text-slate-500 text-sm">/{isAnnual ? 'year' : 'month'}</span>
            </div>
            <div className="flex items-center gap-2 mt-1">
              <span className="text-sm text-slate-400">(~${priceUSD}/{isAnnual ? 'year' : 'month'})</span>
              {isAnnual && monthlyEq && (
                <span className="text-xs px-2 py-0.5 bg-green-100 text-green-700 rounded-full font-medium">
                  ₹{monthlyEq.inr.replace('₹', '')}/mo
                </span>
              )}
            </div>
          </div>
        )}
      </div>

      {/* Features */}
      <ul className="space-y-2.5 mb-6 flex-1">
        {plan.features.map((feature, i) => (
          <li key={i} className="flex items-start gap-2.5">
            {feature.included ? (
              <Check className={`w-4 h-4 mt-0.5 ${colors.accent} flex-shrink-0`} />
            ) : (
              <X className="w-4 h-4 mt-0.5 text-slate-300 flex-shrink-0" />
            )}
            <span className={`text-sm ${feature.included ? 'text-slate-700' : 'text-slate-400'}`}>
              {feature.text}
            </span>
          </li>
        ))}
      </ul>

      {/* CTA Button */}
      <Button
        onClick={() => onSelect(plan)}
        disabled={isCurrentPlan}
        className={`w-full h-11 text-white font-medium rounded-xl ${
          isCurrentPlan ? 'bg-green-600 cursor-default' : colors.btn
        }`}
        data-testid={`select-plan-${plan.id}`}
      >
        {isCurrentPlan ? (
          <div className="flex items-center gap-2"><Check className="w-4 h-4" /> Current Plan</div>
        ) : isFree ? (
          'Get Started Free'
        ) : (
          <div className="flex items-center gap-2">
            {plan.id === 'premium' ? 'Go Premium' : 'Upgrade Now'}
            <ArrowRight className="w-4 h-4" />
          </div>
        )}
      </Button>
    </div>
  );
}

export default function PricingPage() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [userType, setUserType] = useState('trainer');
  const [billing, setBilling] = useState('monthly');

  const plans = getAllPlans(userType);

  const handleSelectPlan = (plan) => {
    if (!user) {
      toast.info('Please create an account to subscribe');
      navigate('/register');
      return;
    }
    if (plan.priceINR === 0) {
      toast.info('You are already on the Basic plan');
      return;
    }
    // For now, show a message that payment integration is coming soon
    toast.info('Payment gateway coming soon!', {
      description: `${plan.name} plan - ₹${billing === 'annual' ? plan.annualPriceINR.toLocaleString('en-IN') + '/year' : plan.priceINR.toLocaleString('en-IN') + '/month'}. We'll notify you when payments are live.`,
      duration: 5000
    });
  };

  return (
    <div className="min-h-screen bg-white" data-testid="pricing-page">
      <SEO
        title="Pricing Plans"
        description="Affordable training marketplace plans starting from Free. Professional at ₹500/month, Premium at ₹1,000/month. Find and hire expert corporate trainers globally."
        path="/pricing"
      />
      {/* Header */}
      <header className="sticky top-0 bg-white/90 backdrop-blur-md border-b border-slate-100 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex items-center justify-between h-16">
          <Link to="/" className="flex items-center gap-2">
            <div className="w-9 h-9 bg-blue-600 rounded-lg flex items-center justify-center">
              <Briefcase className="w-5 h-5 text-white" />
            </div>
            <span className="font-outfit font-bold text-xl text-slate-900">Training Solutions</span>
          </Link>
          <div className="flex items-center gap-4">
            <Link to="/search?type=trainers" className="text-sm text-slate-600 hover:text-slate-900 font-medium hidden sm:block">Find Trainers</Link>
            <Link to="/search?type=jobs" className="text-sm text-slate-600 hover:text-slate-900 font-medium hidden sm:block">Browse Requirements</Link>
            {user ? (
              <Link to={user.user_type === 'trainer' ? '/trainer/dashboard' : '/corporate/dashboard'}>
                <Button className="bg-blue-600 hover:bg-blue-700 text-white text-sm h-9 px-4 rounded-lg" data-testid="go-dashboard-btn">
                  Dashboard
                </Button>
              </Link>
            ) : (
              <Link to="/register">
                <Button className="bg-blue-600 hover:bg-blue-700 text-white text-sm h-9 px-4 rounded-lg" data-testid="get-started-btn">
                  Get Started
                </Button>
              </Link>
            )}
          </div>
        </div>
      </header>

      {/* Hero */}
      <section className="py-16 px-4">
        <div className="max-w-4xl mx-auto text-center">
          <h1 className="font-outfit text-4xl sm:text-5xl font-bold text-slate-900 mb-4" data-testid="pricing-heading">
            Simple, Transparent Pricing
          </h1>
          <p className="text-slate-500 text-base sm:text-lg max-w-2xl mx-auto mb-8">
            Choose the plan that fits your needs. Start free and upgrade as you grow. All prices in INR with USD equivalent.
          </p>

          {/* User Type Toggle */}
          <div className="flex items-center justify-center gap-3 mb-6">
            <button
              onClick={() => setUserType('trainer')}
              className={`flex items-center gap-2 px-5 py-2.5 rounded-xl font-medium text-sm transition-all ${
                userType === 'trainer' ? 'bg-blue-600 text-white shadow-md' : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
              }`}
              data-testid="toggle-trainer"
            >
              <User className="w-4 h-4" /> For Trainers
            </button>
            <button
              onClick={() => setUserType('corporate')}
              className={`flex items-center gap-2 px-5 py-2.5 rounded-xl font-medium text-sm transition-all ${
                userType === 'corporate' ? 'bg-emerald-600 text-white shadow-md' : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
              }`}
              data-testid="toggle-corporate"
            >
              <Users className="w-4 h-4" /> For Companies
            </button>
          </div>

          {/* Billing Toggle */}
          <div className="inline-flex items-center gap-1 p-1 bg-slate-100 rounded-xl" data-testid="billing-toggle">
            <button
              onClick={() => setBilling('monthly')}
              className={`px-4 py-2 rounded-lg text-sm font-medium transition-all ${
                billing === 'monthly' ? 'bg-white text-slate-900 shadow-sm' : 'text-slate-500 hover:text-slate-700'
              }`}
              data-testid="billing-monthly"
            >
              Monthly
            </button>
            <button
              onClick={() => setBilling('annual')}
              className={`px-4 py-2 rounded-lg text-sm font-medium transition-all flex items-center gap-1.5 ${
                billing === 'annual' ? 'bg-white text-slate-900 shadow-sm' : 'text-slate-500 hover:text-slate-700'
              }`}
              data-testid="billing-annual"
            >
              Annual
              <span className="text-xs px-1.5 py-0.5 bg-green-100 text-green-700 rounded-full font-bold">-20%</span>
            </button>
          </div>
        </div>
      </section>

      {/* Plan Cards */}
      <section className="pb-20 px-4">
        <div className="max-w-5xl mx-auto grid md:grid-cols-3 gap-6">
          {plans.map((plan) => (
            <PlanCard
              key={plan.id}
              plan={plan}
              billing={billing}
              userType={userType}
              isCurrentPlan={user?.subscription_plan === plan.id}
              onSelect={handleSelectPlan}
            />
          ))}
        </div>

        {/* Payment Coming Soon Notice */}
        <div className="max-w-3xl mx-auto mt-12 text-center">
          <div className="inline-flex items-center gap-2 px-5 py-3 bg-amber-50 border border-amber-200 rounded-xl" data-testid="payment-coming-soon">
            <CreditCard className="w-5 h-5 text-amber-600" />
            <p className="text-sm text-amber-700 font-medium">
              Payment gateway integration coming soon. Plans will be activated once payments go live.
            </p>
          </div>
        </div>

        {/* FAQ Section */}
        <div className="max-w-3xl mx-auto mt-16">
          <h2 className="font-outfit text-2xl font-bold text-slate-900 text-center mb-8">Frequently Asked Questions</h2>
          <div className="space-y-4">
            {[
              { q: 'Can I change plans anytime?', a: 'Yes, you can upgrade or downgrade your plan at any time. Changes take effect immediately.' },
              { q: 'Is there a refund policy?', a: 'Yes, we offer a 7-day money-back guarantee on all paid plans. No questions asked.' },
              { q: 'Do you accept international payments?', a: 'Yes, we accept payments from across the world via credit/debit cards and international payment methods. Prices shown in both INR and USD.' },
              { q: 'What happens when my subscription expires?', a: 'Your account reverts to the Basic (free) plan. All your data is preserved, and you can resubscribe anytime.' },
            ].map((faq, i) => (
              <div key={i} className="p-4 bg-slate-50 rounded-xl border border-slate-200" data-testid={`faq-${i}`}>
                <h4 className="font-medium text-slate-900 mb-1">{faq.q}</h4>
                <p className="text-sm text-slate-500">{faq.a}</p>
              </div>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
}
